<template>
  <div>
    <EventAttendAlert
      :attendStatus="eventAttendStatus"
      :guestCount="currentUserGuestsCount"
      :guestsAllowed="guestsAllowed"
      @toggleAttending="toggleAttending($event)"
    ></EventAttendAlert>
    <v-tabs v-model="tab" background-color="transparent" centered>
      <v-tab key="attend">{{ t$(".tabs.attendTab") }}</v-tab>
      <v-tab key="decline">{{ t$(".tabs.declineTab") }}</v-tab>
      <v-tab key="pending">Keine Antwort</v-tab>
    </v-tabs>
    <v-tabs-items
      v-model="tab"
      style="background-color: transparent; margin-top: 16px"
    >
      <v-tab-item key="attend">
        <div class="d-flex justify-center">
          <AddEditAttendeeDialog
            v-if="isCurrentMemberOrganizerOrAllowed"
            :response="RsvpReplyAttend"
            :fetchAttendees="getSearchFunction"
            :guestsAllowed="guestsAllowed"
            :viewContext="viewContext"
            :selected-items="[
              ...coetanAttendees,
              ...nonCoetans,
              ...declinedAttendees,
            ]"
            @save="handleCreateAttendee($event)"
          >
            <template v-slot="{ click, attrs }">
              <Button
                :btn-label="t$('components.add_attendee_button.label')"
                @click="click"
                class="mb-4"
              ></Button>
            </template>
          </AddEditAttendeeDialog>
        </div>
        <EventAttendeeListSection
          class="mb-8"
          :attendees="coetanAttendees"
          :attendeesLoading="loading"
          :loading-more="loadingMore"
          :title="AttendingCoetanenSubtitle"
          :guestsAllowed="guestsAllowed"
          :is-load-more-available="false"
          :is-editable="isCurrentMemberOrganizerOrAllowed"
          :view-context="viewContext"
          @editAttendee="handleAttendeeEdit"
          @deleteAttendee="handleAttendeeDelete"
        ></EventAttendeeListSection>
        <EventAttendeeListSection
          class="mb-8"
          :attendees="nonCoetans"
          :attendees-loading="loading"
          :loading-more="loadingMore"
          :title="AttendingMembersSubtitle"
          :guestsAllowed="guestsAllowed"
          :is-editable="isCurrentMemberOrganizerOrAllowed"
          :is-load-more-available="isMoreRSVPAvailable"
          :view-context="viewContext"
          @loadMore="loadMoreRSVP"
          @editAttendee="handleAttendeeEdit"
          @deleteAttendee="handleAttendeeDelete"
        ></EventAttendeeListSection>
      </v-tab-item>
      <v-tab-item key="decline">
        <div class="d-flex justify-center">
          <AddEditAttendeeDialog
            v-if="isCurrentMemberOrganizerOrAllowed"
            :response="RsvpReplyDecline"
            :fetchAttendees="getSearchFunction"
            :guestsAllowed="guestsAllowed"
            :selected-items="[
              ...coetanAttendees,
              ...nonCoetans,
              ...declinedAttendees,
            ]"
            @save="handleCreateAttendee($event)"
          >
            <template v-slot="{ click, attrs }">
              <Button
                :btn-label="t$('components.add_attendee_button.label')"
                @click="click"
                class="mb-4"
              ></Button>
            </template>
          </AddEditAttendeeDialog>
        </div>
        <EventAttendeeListSection
          class="mb-8 mt-2"
          :attendees="declinedAttendees"
          :attendees-loading="loading"
          :title="DeclinedMemberSubtitle"
          :loading-more="loadingMore"
          :guestsAllowed="guestsAllowed"
          :is-editable="isCurrentMemberOrganizerOrAllowed"
          :is-load-more-available="isMoreDeclinedRSVPAvailable"
          :view-context="viewContext"
          @loadMore="loadMoreDeclinedRSVP"
          @editAttendee="handleAttendeeEdit"
          @deleteAttendee="handleAttendeeDelete"
        ></EventAttendeeListSection>
      </v-tab-item>
      <v-tab-item key="pending">
        <div class="d-flex justify-center">
          <AddEditAttendeeDialog
            v-if="isCurrentMemberOrganizerOrAllowed"
            :response="RsvpReplyPending"
            :fetchAttendees="getSearchFunction"
            :guestsAllowed="guestsAllowed"
            :selected-items="[
              ...coetanAttendees,
              ...nonCoetans,
              ...declinedAttendees,
            ]"
            @save="handleCreateAttendee($event)"
          >
            <template v-slot="{ click, attrs }">
              <Button
                :btn-label="t$('components.add_attendee_button.label')"
                @click="click"
                class="mb-4"
              ></Button>
            </template>
          </AddEditAttendeeDialog>
        </div>
        <EventAttendeeListSection
          class="mb-8 mt-2"
          :attendees="pendingAttendees"
          :attendees-loading="loading"
          :title="PendingMembersSubtitle"
          :guestsAllowed="guestsAllowed"
          :is-editable="isCurrentMemberOrganizerOrAllowed"
          :is-load-more-available="isMorePendingRSVPAvailable"
          :loading-more="loadingMore"
          :view-context="viewContext"
          @loadMore="loadMorePendingRSVP"
          @editAttendee="handleAttendeeEdit"
          @deleteAttendee="handleAttendeeDelete"
        />
      </v-tab-item>
    </v-tabs-items>
    <TextBody
      v-if="nonCoetans.length === 0 && coetanAttendees.length === 0"
      class="text-center mb-4"
      :text="noAttendeesText"
    ></TextBody>
  </div>
</template>

<script lang="ts">
import { EVENT_STATUS } from "constants/constants"
import { createNamespacedHelpers } from "vuex"

const { mapGetters } = createNamespacedHelpers("event")
const { mapGetters: mapMemberGetters } = createNamespacedHelpers("member")

import { mdiAccountMultipleOutline } from "@mdi/js"
import StatusIcon from "../UI/StatusIcon.vue"
import Header from "../UI/Header.vue"
import TextButton from "../UI/TextButton.vue"
import TextBody from "../UI/TextBody.vue"
import AvatarCard from "../UI/AvatarCard.vue"
import Subtitle from "../UI/Subtitle.vue"
import GuestModal from "./GuestModal.vue"
import { computed, defineComponent } from "vue"
import EventAttendAlert from "features/events/attendees/event-attend-alert/EventAttendAlert.vue"
import useTranslate from "features/common/translate/concerns/useTranslate"
import AddEditAttendeeDialog from "features/events/attendees/add-edit-attendee/AddEditAttendeeDialog.vue"
import Button from "components/UI/Button.vue"
import { membersApi } from "store/api"
import { Member, RsvpReply } from "suus-api"
import { useRSVPContext } from "app/modules/events/rsvp-context/use-rsvp-context.concern"
import { useOwnUserAttendanceContext } from "app/modules/events/use-own-user-attendance-context.concern"
import { ViewContext } from "concerns/useContextName"
import EventAttendeeListSection from "features/events/attendees/event-attendee-list/EventAttendeeListSection.vue"
import { GroupAccess } from "components/TopicForm/general-fields/group-access"
import { useSnackbar } from "app/ui-primitives/snackbars/SnackbarProvider.vue"

export default defineComponent({
  name: "EventAttendeeList",
  components: {
    Button,
    AddEditAttendeeDialog,
    EventAttendAlert,
    StatusIcon,
    Header,
    TextButton,
    TextBody,
    AvatarCard,
    Subtitle,
    GuestModal,
    EventAttendeeListSection,
  },
  data: () => ({
    EVENT_STATUS: EVENT_STATUS,
    guestsIcon: mdiAccountMultipleOutline,
    tab: null,
  }),
  computed: {
    eventId(): string {
      return this.$route.params.event_id
    },
    AttendingCoetanenSubtitle(): string {
      if (this.coetanenCount < 1) return null
      const component = ".coetanen_subtitle"
      return this.currentUserAttends
        ? this.tc$(component + ".attending", this.coetanenCount)
        : this.t$(component + ".other", [this.coetanenCount])
    },
    DeclinedMemberSubtitle(): string {
      if (this.declinesCount < 1) return null
      return this.t$(".declined_subtitle", [this.declinesCount])
    },
    PendingMembersSubtitle(): string {
      if (this.pendingsCount < 1) return `Keine ausstehenden Antworten`
      return `${this.pendingsCount} ausstehende Antworten`
    },
    AttendingMembersSubtitle(): string {
      if (this.nonCoetans.length < 1) return null
      const component = ".members_subtitle"
      const other =
        this.coetanenCount > 0
          ? this.tc$(component + ".other", this.nonCoetansCount)
          : ""
      return this.tc$(component + ".members", this.nonCoetansCount, {
        other: other,
      })
    },
    noAttendeesText(): string {
      return this.t("no_attendees")
    },
    isCurrentMemberOrganizerOrAllowed(): boolean {
      return this.currentEvent?.links.update_url != null
    },
    getSearchFunction(): (query: any) => Promise<Member[]> {
      // TODO: Remove store; Doesnt belong to RSVP directly so ignored in [SN-1265]
      if (
        this.currentEvent?.nonmember_access === GroupAccess.CONFIDENTIAL ||
        this.currentEvent?.nonmember_access === GroupAccess.INTERNAL
      ) {
        return this.buildQueryFunction(
          undefined,
          undefined,
          this.currentEvent?.group.id
        )
      } else {
        return this.buildQueryFunction(
          this.currentEvent?.group.client_id,
          this.currentEvent?.group.level_id
        )
      }
    },
    viewContext(): ViewContext {
      // TODO: Remove store; Doesnt belong to RSVP directly so ignored in [SN-1265]
      return {
        level_id: this.currentEvent?.level_id,
        group: this.currentEvent?.group,
        client_id: this.currentEvent?.client_id,
      }
    },
    ...mapGetters([
      // TODO: Remove store; guestsAllowed doesn't belong to RSVP directly so ignored in [SN-1265]
      "guestsAllowed",
      // TODO: Remove store; currentEvent doesn't belong to RSVP directly so ignored in [SN-1265]
      "currentEvent",
    ]),
    // TODO: Remove store; currentMember doesn't belong to RSVP directly so ignored in [SN-1265]
    ...mapMemberGetters(["currentMember"]),
  },
  methods: {
    async handleCreateAttendee(saveAttendeeObject) {
      const { attendee, response, guest_count } = saveAttendeeObject
      const personal_record_id = attendee.personal_record_id
      try {
        await this.createRSVP(personal_record_id, response, guest_count)
      } catch (error) {
        this.showError("Einladung konnte nicht erstellt werden")
      }
    },
    handleAttendeeEdit(saveAttendeeObject) {
      const { attendee, response, guest_count } = saveAttendeeObject
      const personal_record_id = attendee.personal_record_id
      try {
        this.updateRSVP(personal_record_id, response, guest_count)
      } catch (error) {
        this.showError("Einladung konnte nicht aktualisiert werden")
      }
    },
    async handleAttendeeDelete(personal_record_id) {
      try {
        await this.deleteRSVP(personal_record_id)
      } catch (error) {
        this.showError("Einladung konnte nicht gelöscht werden")
      }
    },
    toggleAttending(isAttend) {
      if (isAttend) {
        this.attend(this.currentUserGuestsCount)
      } else {
        this.decline()
      }
    },
  },
  setup() {
    const { t, t$, tc$ } = useTranslate("components.attendee_list")
    const buildQueryFunction: (
      clientId,
      levelId,
      groupId?
    ) => (query: any) => Promise<Member[]> =
      (clientId, levelId, groupId) => (query) =>
        membersApi
          .fuzzySearch(
            query,
            levelId ? levelId : undefined,
            clientId ? [clientId] : undefined,
            groupId ? [groupId] : undefined,
            true
          )
          .then((response) => response.data.results)

    const RsvpReplyAttend = RsvpReply.Attend
    const RsvpReplyDecline = RsvpReply.Decline
    const RsvpReplyPending = RsvpReply.Pending

    const rsvpConcern = useRSVPContext()

    const {
      countExpectedByReply,
      countByReplyAndSection,
      byReply,
      byReplyAndSection,
      allLoaded,
      loading,
      loadingMore,
      createRSVP,
      updateRSVP,
      deleteRSVP,
      loadMore: loadMoreRSVPs,
    } = rsvpConcern

    const nonCoetans = byReplyAndSection("attend", "nonCoetans")
    const nonCoetansCount = countByReplyAndSection("attend", "nonCoetans")
    const coetanenCount = countByReplyAndSection("attend", "coetans")
    const coetanAttendees = byReplyAndSection("attend", "coetans")

    const {
      ownUserAttends,
      ownUserGuestsCount,
      eventAttendStatus,
      ownUserId,
      updateAttendance,
    } = useOwnUserAttendanceContext()

    const attend = async (guestsCount) => {
      await updateAttendance(() =>
        updateRSVP(parseInt(ownUserId.value), "attend", guestsCount)
      )
    }

    const decline = async () => {
      await updateAttendance(() =>
        updateRSVP(parseInt(ownUserId.value), "decline")
      )
    }

    const areMoreAttendsAvailable = computed(() => {
      console.log(allLoaded.value["attend"])
      return !allLoaded.value["attend"]
    })
    const areMoreDeclinesAvailable = computed(() => !allLoaded.value["decline"])
    const areMorePendingsAvailable = computed(() => !allLoaded.value["pending"])

    const { showError } = useSnackbar()

    return {
      t,
      t$,
      tc$,
      buildQueryFunction,
      loadMoreRSVP: () => loadMoreRSVPs("attend"),
      isMoreRSVPAvailable: areMoreAttendsAvailable,
      loadMoreDeclinedRSVP: () => loadMoreRSVPs("decline"),
      isMoreDeclinedRSVPAvailable: areMoreDeclinesAvailable,
      loadMorePendingRSVP: () => loadMoreRSVPs("pending"),
      isMorePendingRSVPAvailable: areMorePendingsAvailable,
      RsvpReplyAttend,
      RsvpReplyDecline,
      RsvpReplyPending,
      // New Concerns
      coetanenCount,
      currentUserAttends: ownUserAttends,
      declinesCount: countExpectedByReply("decline"),
      pendingsCount: countExpectedByReply("pending"),
      nonCoetans,
      currentUserGuestsCount: ownUserGuestsCount,
      loading,
      loadingMore,
      coetanAttendees,
      declinedAttendees: byReply("decline"),
      pendingAttendees: byReply("pending"),
      createRSVP: createRSVP,
      updateRSVP: updateRSVP,
      deleteRSVP,
      eventAttendStatus,
      attend,
      decline,
      nonCoetansCount,
      showError,
    }
  },
})
</script>
